<script setup lang="ts">
import { provide, ref, watch } from 'vue'

export interface TabItem {
  key: string
  label: string
  disabled?: boolean
}

const props = withDefaults(
  defineProps<{
    activeKey?: string
    modelValue?: string
    items?: TabItem[]
    type?: 'line' | 'card'
    size?: 'default' | 'small'
  }>(),
  {
    activeKey: undefined,
    modelValue: undefined,
    items: () => [],
    type: 'line',
    size: 'default'
  }
)

const emit = defineEmits<{
  'update:activeKey': [key: string]
  'update:modelValue': [key: string]
  change: [key: string]
}>()

const currentActiveKey = ref(props.activeKey ?? props.modelValue ?? (props.items[0]?.key || ''))

watch(
  () => props.activeKey ?? props.modelValue,
  (val) => {
    if (val !== undefined) currentActiveKey.value = val
  }
)

function setActiveTab(key: string, disabled?: boolean) {
  if (disabled) return
  currentActiveKey.value = key
  emit('update:activeKey', key)
  emit('update:modelValue', key)
  emit('change', key)
}

provide('activeTabKey', currentActiveKey)
</script>

<template>
  <div class="rr-tabs" :class="[`rr-tabs--${type}`, `rr-tabs--${size}`]">
    <div class="rr-tabs__nav" role="tablist">
      <button
        v-for="item in items"
        :key="item.key"
        type="button"
        role="tab"
        :aria-selected="currentActiveKey === item.key"
        :disabled="item.disabled"
        class="rr-tabs__tab"
        :class="{
          'is-active': currentActiveKey === item.key,
          'is-disabled': item.disabled
        }"
        @click="setActiveTab(item.key, item.disabled)"
      >
        <span>{{ item.label }}</span>
      </button>
    </div>

    <div class="rr-tabs__content">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.rr-tabs {
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

.rr-tabs__nav {
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid var(--rr-color-border);
  margin-bottom: 16px;
}

.rr-tabs__tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: unset !important;
  padding: 10px 16px !important;
  margin: 0 !important;
  border: 0;
  background: transparent !important;
  color: var(--rr-color-text-muted) !important;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  outline: 0;
  transition: all 150ms ease;
}

.rr-tabs__tab:hover:not(:disabled) {
  color: var(--rr-color-primary) !important;
}

.rr-tabs__tab.is-active {
  color: var(--rr-color-primary) !important;
  font-weight: 600;
}

.rr-tabs--line .rr-tabs__tab.is-active::after {
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--rr-color-primary);
  content: "";
}

.rr-tabs--card .rr-tabs__nav {
  gap: 4px;
}

.rr-tabs--card .rr-tabs__tab {
  border: 1px solid var(--rr-color-border);
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  background: var(--rr-color-surface-subtle);
  padding: 8px 14px;
}

.rr-tabs--card .rr-tabs__tab.is-active {
  background: var(--rr-color-surface);
  border-color: var(--rr-color-border);
  border-top: 2px solid var(--rr-color-primary);
}

.rr-tabs--small .rr-tabs__tab {
  padding: 6px 10px;
  font-size: 12px;
}

.rr-tabs__tab:disabled {
  color: var(--rr-color-text-subtle);
  cursor: not-allowed;
}

.rr-tabs__content {
  flex: 1 1 auto;
}
</style>
